/**
 * Encryption/Decryption utilities for secure API communication
 * Uses Web Crypto API (browser-compatible)
 *
 * Algorithm: AES-256-GCM
 * Format: salt:iv:authTag:encrypted (all hex-encoded)
 */

const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 16;
const SALT_LENGTH = 64;
const TAG_LENGTH = 16; // 128 bits for GCM auth tag
const PBKDF2_ITERATIONS = 100000;

/**
 * Get encryption key from environment or config
 */
function getEncryptionKey() {
  return process.env.REACT_APP_ENCRYPTION_KEY || 'your-32-character-encryption-key-change-in-production!!';
}

/**
 * Convert string to ArrayBuffer
 */
function str2ab(str) {
  const encoder = new TextEncoder();
  return encoder.encode(str);
}

/**
 * Convert ArrayBuffer to string
 */
function ab2str(buffer) {
  const decoder = new TextDecoder();
  return decoder.decode(buffer);
}

/**
 * Convert ArrayBuffer to hex string
 */
function arrayBufferToHex(buffer) {
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Convert hex string to ArrayBuffer
 */
function hexToArrayBuffer(hex) {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
  }
  return bytes.buffer;
}

/**
 * Derive encryption key using PBKDF2
 */
async function deriveKey(password, salt) {
  const passwordBuffer = str2ab(password);

  // Import password as key material
  const keyMaterial = await crypto.subtle.importKey('raw', passwordBuffer, { name: 'PBKDF2' }, false, ['deriveBits', 'deriveKey']);

  // Derive key using PBKDF2
  return await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-512'
    },
    keyMaterial,
    { name: ALGORITHM, length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt data using AES-256-GCM
 * @param {any} data - Data to encrypt (will be JSON stringified if not string)
 * @returns {string} - Encrypted data in format: salt:iv:authTag:encrypted
 */
export async function encrypt(data) {
  try {
    const text = typeof data === 'string' ? data : JSON.stringify(data);
    const encryptionKey = getEncryptionKey();

    // Generate random salt and IV
    const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

    // Derive key from password and salt
    const key = await deriveKey(encryptionKey, salt);

    // Encrypt data using AES-GCM
    // The Web Crypto API automatically appends the auth tag to the ciphertext
    const encryptedWithTag = await crypto.subtle.encrypt(
      {
        name: ALGORITHM,
        iv: iv,
        tagLength: 128 // 128-bit auth tag (16 bytes)
      },
      key,
      str2ab(text)
    );

    // In Web Crypto API's AES-GCM, the auth tag is appended to the end
    // Extract the last 16 bytes as the auth tag
    const encryptedArray = new Uint8Array(encryptedWithTag);
    const ciphertextLength = encryptedArray.length - TAG_LENGTH;
    const ciphertext = encryptedArray.slice(0, ciphertextLength);
    const authTag = encryptedArray.slice(ciphertextLength);

    // Format: salt:iv:authTag:encrypted (all in hex)
    return [arrayBufferToHex(salt), arrayBufferToHex(iv), arrayBufferToHex(authTag), arrayBufferToHex(ciphertext)].join(':');
  } catch (error) {
    console.error('Encryption error:', error);
    throw new Error(`Encryption failed: ${error.message}`);
  }
}

/**
 * Decrypt data using AES-256-GCM
 * @param {string} encryptedData - Encrypted data in format: salt:iv:authTag:encrypted
 * @returns {any} - Decrypted and parsed data
 */
export async function decrypt(encryptedData) {
  try {
    const parts = encryptedData.split(':');

    if (parts.length !== 4) {
      throw new Error('Invalid encrypted data format - expected 4 parts (salt:iv:authTag:encrypted)');
    }

    const [saltHex, ivHex, authTagHex, encryptedHex] = parts;

    if (!saltHex || !ivHex || !authTagHex || !encryptedHex) {
      throw new Error('Invalid encrypted data format - missing components');
    }

    const encryptionKey = getEncryptionKey();

    // Convert hex strings back to ArrayBuffers
    const salt = hexToArrayBuffer(saltHex);
    const iv = hexToArrayBuffer(ivHex);
    const authTag = hexToArrayBuffer(authTagHex);
    const ciphertext = hexToArrayBuffer(encryptedHex);

    // Derive key from password and salt
    const key = await deriveKey(encryptionKey, salt);

    // Web Crypto API expects ciphertext + authTag concatenated
    const encryptedWithTag = new Uint8Array(ciphertext.byteLength + authTag.byteLength);
    encryptedWithTag.set(new Uint8Array(ciphertext), 0);
    encryptedWithTag.set(new Uint8Array(authTag), ciphertext.byteLength);

    // Decrypt data
    const decrypted = await crypto.subtle.decrypt(
      {
        name: ALGORITHM,
        iv: new Uint8Array(iv),
        tagLength: 128
      },
      key,
      encryptedWithTag
    );

    // Convert to string and parse JSON
    const decryptedText = ab2str(decrypted);

    // Try to parse as JSON, return as string if it fails
    try {
      return JSON.parse(decryptedText);
    } catch {
      return decryptedText;
    }
  } catch (error) {
    console.error('Decryption error:', error);
    throw new Error(`Decryption failed: ${error.message}`);
  }
}

/**
 * Check if encryption should be used
 * Encryption is enabled by default and only bypassed if COMMUNICATION_KEY is provided
 */
export function shouldEncrypt() {
  const communicationKey = process.env.REACT_APP_COMMUNICATION_KEY;
  // If communication key is set and not empty, don't encrypt (trusted service)
  // Otherwise, always encrypt
  return !communicationKey || communicationKey.trim() === '';
}

/**
 * Get communication key for bypass header
 */
export function getCommunicationKey() {
  const key = process.env.REACT_APP_COMMUNICATION_KEY;
  // Only return the key if it's not empty
  return key && key.trim() !== '' ? key : null;
}
